import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Header from "../components/Header";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}

const getProxiedImageUrl = (url) => {
  if (!url) return "";
  if (url.includes("supabase.co")) {
    return `${API_URL}/creators/proxy-image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

export default function Profile() {
  const [profile, setProfile] = useState({ name: "", email: "", bio: "", profile_image: "" });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/creators/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      localStorage.setItem("creator", JSON.stringify(res.data));
    } catch (err) {
      console.error("Fetch profile error:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      setMessage("Failed to load profile. Please refresh the page.");
    } finally {
      setInitialLoading(false);
    }
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        setMessage("File too large. Maximum size is 10MB.");
        return;
      }
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setTempImageUrl(reader.result);
        setShowCropper(true);
      });
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  const getCroppedImg = () => {
    return new Promise((resolve) => {
      if (!completedCrop || !imgRef.current) {
        resolve(null);
        return;
      }
      const image = imgRef.current;
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const file = new File([blob], "cropped_profile.jpg", { type: "image/jpeg" });
        resolve(file);
      }, "image/jpeg", 0.9);
    });
  };

  const handleCropComplete = async () => {
    if (!completedCrop) {
      alert("Please select a crop area before continuing.");
      return;
    }
    setUploading(true);
    const croppedFile = await getCroppedImg();
    if (croppedFile) {
      setImageFile(croppedFile);
      setShowCropper(false);
      setTempImageUrl(null);
    } else {
      alert("Cropping failed. Using original image.");
    }
    setUploading(false);
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setTempImageUrl(null);
    setImageFile(null);
    setCompletedCrop(null);
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    const formData = new FormData();
    formData.append("image", imageFile);
    try {
      const res = await axios.post(`${API_URL}/creators/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      return res.data.imageUrl;
    } catch (err) {
      console.error("Upload error:", err);
      const errorMsg = err.response?.data?.error || "Image upload failed";
      setMessage(errorMsg);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    let profileImage = profile.profile_image;

    if (imageFile) {
      const uploadedUrl = await uploadImage();
      if (uploadedUrl) {
        profileImage = uploadedUrl;
        setProfile(prev => ({ ...prev, profile_image: profileImage }));
        const creator = JSON.parse(localStorage.getItem("creator") || "{}");
        localStorage.setItem("creator", JSON.stringify({ ...creator, profile_image: profileImage }));
      } else {
        setLoading(false);
        return;
      }
    }

    try {
      await axios.put(`${API_URL}/creators/profile`,
        { ...profile, profile_image: profileImage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage("Profile updated successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Update profile error:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      setMessage("Update failed. Please try again.");
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading profile...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="text-3xl font-bold text-primary mb-6">Edit Profile</h1>
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">Profile Photo</label>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20">
                {profile.profile_image && (
                  <img
                    src={getProxiedImageUrl(profile.profile_image)}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                    onError={(e) => e.target.style.display = "none"}
                  />
                )}
                {!profile.profile_image && (
                  <div className="w-20 h-20 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-2xl font-bold">
                    {profile.name?.charAt(0) || "?"}
                  </div>
                )}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={onSelectFile} className="text-sm" />
                <p className="text-xs text-gray-500 mt-1">JPG or PNG, max 10MB. You can crop after selection.</p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Name</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Email</label>
            <input
              type="email"
              value={profile.email}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-1">Bio (optional)</label>
            <textarea
              value={profile.bio || ""}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {message && (
            <div className={`mb-4 p-3 rounded-md text-center ${message.includes("success") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || uploading}
            className="w-full bg-primary text-white py-2 rounded-md font-semibold hover:bg-opacity-90 transition disabled:opacity-50"
          >
            {uploading ? "Processing image..." : loading ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </main>

      {/* Crop Modal */}
      {showCropper && tempImageUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={handleCancelCrop}>
          <div className="bg-white rounded-lg max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-primary mb-3">Crop Profile Picture</h3>
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              circularCrop
              keepSelection
            >
              <img
                ref={imgRef}
                src={tempImageUrl}
                alt="Crop preview"
                className="max-w-full max-h-[50vh] object-contain"
                onLoad={(e) => {
                  const { width, height } = e.currentTarget;
                  const newCrop = centerAspectCrop(width, height, 1);
                  setCrop(newCrop);
                }}
              />
            </ReactCrop>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={handleCancelCrop} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100">Cancel</button>
              <button onClick={handleCropComplete} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90">Crop & Upload</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}